import { Link } from "react-router-dom";
import { LogOut, Sparkles, Sun, Moon, ChevronRight, Globe2, BellRing, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/store/AuthContext";
import { useTheme } from "@/store/ThemeContext";
import { useMyGroup, useMyHabits, useMySegmentation, useMyPreferences, useUpdatePreferences } from "@/lib/hooks";
import { Switch } from "@/components/ui/switch";

export default function Profile() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const { data: group } = useMyGroup();
  const { data: habits = [] } = useMyHabits();
  const { data: seg } = useMySegmentation();
  const { data: prefs } = useMyPreferences();
  const updatePrefs = useUpdatePreferences();

  const isPremium = !!user?.is_premium;
  const autoNudge = !!prefs?.auto_nudge;
  const forced = !!prefs?.auto_nudge_forced;
  const missCount = prefs?.consecutive_misses ?? 0;

  const onToggleAutoNudge = async (checked) => {
    try {
      await updatePrefs.mutateAsync({ auto_nudge: checked });
      toast(checked ? "Auto-nudge is on." : "Auto-nudge is off.", {
        description: checked
          ? "Your circle will be pinged when your habit starts."
          : "You'll send nudges manually from now on.",
      });
    } catch (e) {
      toast.error("Couldn't save", { description: e?.response?.data?.detail || e.message });
    }
  };

  return (
    <div className="space-y-8" data-testid="profile-page">
      <header className="flex items-center gap-5">
        {user?.picture ? (
          <img src={user.picture} alt={user.name} className="w-20 h-20 rounded-full object-cover border border-stone-200" referrerPolicy="no-referrer" />
        ) : (
          <div className="w-20 h-20 rounded-full bg-charcoal text-paper flex items-center justify-center font-serif text-3xl">
            {user?.name?.[0] || "·"}
          </div>
        )}
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h1 className="font-serif text-4xl text-stone-900 leading-tight">{user?.name || "You"}</h1>
            {isPremium && (
              <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest bg-gradient-to-r from-amber-400 to-yellow-500 text-amber-950" data-testid="profile-pro-badge">
                <Sparkles size={10} /> Pro
              </span>
            )}
          </div>
          <p className="text-sm text-stone-500 mt-1">{user?.email}</p>
        </div>
      </header>

      <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card-editorial p-5" data-testid="profile-stat-circle">
          <span className="eyebrow">Your circle</span>
          <p className="font-serif text-2xl text-stone-900 mt-1 leading-tight">{group?.name || "—"}</p>
          <p className="data-mono text-sm text-terracotta mt-1">{group?.myMembership?.matchScore ?? "—"}% match</p>
          <Link to="/community" className="text-xs text-terracotta hover:underline mt-3 inline-flex items-center">See circle <ChevronRight size={12} /></Link>
        </div>
        <div className="card-editorial p-5" data-testid="profile-stat-habits">
          <span className="eyebrow">Your rhythm</span>
          <p className="font-serif text-2xl text-stone-900 mt-1">{habits.length} habit{habits.length === 1 ? "" : "s"}</p>
          <p className="text-xs text-stone-500 mt-1">On your list right now.</p>
          <Link to="/library" className="text-xs text-terracotta hover:underline mt-3 inline-flex items-center">Add another <ChevronRight size={12} /></Link>
        </div>
        <div className="card-editorial p-5" data-testid="profile-stat-signals">
          <span className="eyebrow">Your signals</span>
          <p className="font-serif text-xl text-stone-900 mt-1 leading-tight">{seg?.age} · {seg?.timezone}</p>
          <p className="text-xs text-stone-500 mt-1 flex items-center gap-1"><Globe2 size={11} /> {seg?.language} · {seg?.religion}</p>
          <Link to="/community" className="text-xs text-terracotta hover:underline mt-3 inline-flex items-center">Edit <ChevronRight size={12} /></Link>
        </div>
      </section>

      <section className="card-editorial p-6" data-testid="profile-premium-panel">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <span className="eyebrow flex items-center gap-2"><Sparkles size={12} /> Premium</span>
            <p className="font-serif text-2xl text-stone-900 mt-1">
              {isPremium ? "You're a Pro member." : "Ad-free, unlimited, exports."}
            </p>
            <p className="text-xs text-stone-500 mt-1">
              {isPremium
                ? `Since ${user.premium_since ? new Date(user.premium_since).toLocaleDateString() : "recently"}.`
                : "One-time $5.99. Removes ads and unlocks calendar export."}
            </p>
          </div>
          {!isPremium && (
            <Link to="/premium" className="btn-accent" data-testid="profile-upgrade-btn">Upgrade</Link>
          )}
        </div>
      </section>

      <section className="card-editorial p-6" data-testid="profile-prefs">
        <span className="eyebrow">Preferences</span>
        <div className="mt-4 divide-y divide-stone-100">
          <div className="py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              {theme === "dark" ? <Moon size={16} /> : <Sun size={16} />}
              <div>
                <p className="text-sm text-stone-900 font-medium">Dark mode</p>
                <p className="text-xs text-stone-500">Match Cadence to the room.</p>
              </div>
            </div>
            <Switch checked={theme === "dark"} onCheckedChange={toggle} data-testid="profile-dark-toggle" />
          </div>

          <div className="py-4 flex items-start justify-between gap-4" data-testid="profile-auto-nudge-row">
            <div className="flex items-start gap-3">
              <BellRing size={16} className="mt-0.5" />
              <div>
                <p className="text-sm text-stone-900 font-medium flex items-center gap-2">
                  Auto-nudge my circle
                  {forced && (
                    <span
                      className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-widest bg-amber-100 text-amber-800"
                      data-testid="profile-auto-nudge-forced-badge"
                      title="Auto-enabled after 3 missed sessions"
                    >
                      <AlertTriangle size={9} /> auto-enabled
                    </span>
                  )}
                </p>
                <p className="text-xs text-stone-500 max-w-sm">
                  When a habit begins, ping every circle member automatically. Turns on for
                  you after {3 - Math.min(missCount, 3)} more consecutive miss{3 - missCount === 1 ? "" : "es"} if left off
                  {missCount > 0 && ` · ${missCount}/3 misses so far`}.
                </p>
              </div>
            </div>
            <Switch
              checked={autoNudge}
              onCheckedChange={onToggleAutoNudge}
              disabled={updatePrefs.isPending}
              data-testid="profile-auto-nudge-toggle"
            />
          </div>

          <div className="py-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-stone-900 font-medium">Sign out</p>
              <p className="text-xs text-stone-500">You&apos;ll return to the landing page.</p>
            </div>
            <button className="btn-outline" onClick={logout} data-testid="profile-signout-btn">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </section>

      <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { to: "/", label: "Today" },
          { to: "/library", label: "Habit Library" },
          { to: "/calendar", label: "Group Calendar" },
          { to: "/onboarding", label: "Re-run Match Setup" },
        ].map((l) => (
          <Link key={l.to} to={l.to} className="card-editorial p-4 text-center hover:-translate-y-0.5 transition-transform" data-testid={`profile-link-${l.to.replace(/\//g,'') || 'home'}`}>
            <p className="font-serif text-lg text-stone-900">{l.label}</p>
          </Link>
        ))}
      </section>
    </div>
  );
}
